/**
 * Sorting a thrown request into the two failures the blog surface renders
 * differently: refused by role, and everything else.
 *
 * The distinction matters because M1-S5 offers no retry — retrying a role
 * refusal changes nothing — while M1-S4 does. `ProtectedRoute` admits every
 * admin role to every URL by design, so a SUPPORT session *can* reach `/blog`;
 * the server is the gate, and this is how its answer reaches the screen.
 *
 * The shared transport throws a plain `Error` for 403 (it reserves its typed
 * error for the subscription codes) and an `ApiRequestError` carrying `status`
 * for everything else. Both shapes are read here rather than in a component, so
 * the day the transport gains a status on its 403 the branch below simply stops
 * being reached.
 */

export type BlogRequestFailure =
  | { kind: 'forbidden'; message: string }
  | { kind: 'failed'; message: string; status: number | null };

const FORBIDDEN_FALLBACK_MESSAGE = 'Access denied';

function statusOf(error: unknown): number | null {
  if (typeof error === 'object' && error !== null && 'status' in error) {
    const status = (error as { status: unknown }).status;
    if (typeof status === 'number') return status;
  }
  return null;
}

function messageOf(error: unknown): string {
  if (error instanceof Error) return error.message;
  return 'The request failed before the server answered.';
}

export function classifyRequestFailure(error: unknown): BlogRequestFailure {
  const status = statusOf(error);
  const message = messageOf(error);

  if (status === 403 || (status === null && message === FORBIDDEN_FALLBACK_MESSAGE)) {
    return { kind: 'forbidden', message };
  }
  return { kind: 'failed', message, status };
}

/** `502 Bad Gateway`-style line for the error panel, or just the message. */
export function describeFailure(failure: BlogRequestFailure, endpoint: string): string {
  if (failure.kind === 'forbidden') {
    return `The server refused ${endpoint} with 403 Forbidden. This surface needs the ADMIN role; the post data was never sent to the browser.`;
  }
  const status = failure.status === null ? 'no status' : String(failure.status);
  return `${endpoint} failed with ${status}. Nothing was changed — this call only reads.`;
}
